import type { AddressManifest, PaymentOption } from '../api/types';
import { config } from '../config';

/**
 * Address verification against a merchant-signed manifest.
 *
 * The merchant dashboard signs `{merchant_id, issued_at, addresses}` (canonical
 * JSON, sorted keys) with an ed25519 key whose public half CoinVello publishes
 * out-of-band (VITE_CV_MANIFEST_PUBKEY, pinned at build time). The checkout
 * page re-derives the message, verifies the signature with WebCrypto, and
 * refuses to mark an address "verified" unless the address shown for the
 * selected option is exactly the signed one. A compromised API can therefore
 * swap addresses but cannot make the UI call them verified.
 *
 * Result: 'verified' | 'mismatch' | 'unverified' (no manifest or no key).
 */
export type ManifestCheck = 'verified' | 'mismatch' | 'unverified';

function canonical(obj: unknown): string {
  if (obj === null || typeof obj !== 'object') return JSON.stringify(obj);
  if (Array.isArray(obj)) return `[${obj.map(canonical).join(',')}]`;
  const rec = obj as Record<string, unknown>;
  return `{${Object.keys(rec)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${canonical(rec[k])}`)
    .join(',')}}`;
}

function hexToBytes(hex: string): Uint8Array<ArrayBuffer> {
  const clean = hex.replace(/^0x/, '');
  const out = new Uint8Array(new ArrayBuffer(clean.length / 2));
  for (let i = 0; i < out.length; i++) out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  return out;
}

const cache = new Map<string, Promise<boolean>>();

async function verifySignature(m: AddressManifest): Promise<boolean> {
  if (!config.manifestPubkey || m.alg !== 'ed25519') return false;
  const key = `${m.signature}`;
  if (!cache.has(key)) {
    cache.set(
      key,
      (async () => {
        try {
          const pub = await crypto.subtle.importKey('raw', hexToBytes(config.manifestPubkey), { name: 'Ed25519' }, false, [
            'verify',
          ]);
          const msg = new TextEncoder().encode(
            canonical({ merchant_id: m.merchant_id, issued_at: m.issued_at, addresses: m.addresses }),
          );
          return await crypto.subtle.verify({ name: 'Ed25519' }, pub, hexToBytes(m.signature), msg);
        } catch {
          return false; // Ed25519 unsupported in this browser or malformed key
        }
      })(),
    );
  }
  return cache.get(key)!;
}

export async function checkOption(manifest: AddressManifest | null | undefined, opt: PaymentOption): Promise<ManifestCheck> {
  if (!manifest || !config.manifestPubkey) return 'unverified';
  const ok = await verifySignature(manifest);
  if (!ok) return 'unverified';
  // Manifest keys are `<asset>:<chain>` asset ids, i.e. exactly `opt.asset`.
  const signed = manifest.addresses[opt.asset] ?? manifest.addresses[`${opt.symbol.toLowerCase()}:${opt.chain}`];
  if (!signed) return 'unverified';
  return signed.toLowerCase() === opt.address.toLowerCase() ? 'verified' : 'mismatch';
}
